import { Component, ElementRef, inject, OnInit, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { QRCodeComponent } from 'angularx-qrcode';
import { AuthenticationService } from '@/app/core/service/auth.service';
import { Store } from '@ngrx/store';
import { getLayoutColor } from '@/app/store/layout/layout-selector';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'TabBar',
  standalone: true,
  imports: [CommonModule, TranslatePipe, RouterModule, QRCodeComponent],
  templateUrl: './tabbar.component.html',
  styles: ``,
})
export class TabBarComponent implements OnInit, OnDestroy {
  @ViewChild('qrCode', { read: ElementRef }) qrCodeElement!: ElementRef;

  currentTheme = localStorage.getItem('theme') ?? 'light';
  private authenticationService = inject(AuthenticationService);
  private store = inject(Store);
  userInfo: any;
  isLoading: boolean = false;
  theme$ = this.store.select(getLayoutColor);
  private modalService = inject(NgbModal);
  private router = inject(Router);
  lastSegment: string = '';

  // cached QR data to avoid recalculation
  private cachedQrData: string | null = null;

  // Subscriptions container
  private subscriptions = new Subscription();

  // Precomputed flags - avoid calling methods from template
  isHomeActive = false;
  isTransactionsActive = false;
  isCalculatorActive = false;
  isUserActive = false;

  // NEW: Check if running in React Native WebView
  private isReactNativeWebView(): boolean {
    return typeof (window as any).ReactNativeWebView !== 'undefined';
  }

  private getCachedQrCode(): string {
    if (!this.cachedQrData) {
      try {
        const userInfo = JSON.parse(localStorage.getItem('userInfo') ?? 'null');
        // safety: check userInfo exists
        this.cachedQrData = JSON.stringify({
          phoneNumber: userInfo?.phoneNumber ?? userInfo?.phoneNumner ?? '',
          areaCode: userInfo?.phoneCode ?? '',
          isBenCashQrCode: true,
        });
      } catch {
        this.cachedQrData = JSON.stringify({
          phoneNumber: '',
          areaCode: '',
          isBenCashQrCode: true,
        });
      }
    }
    return this.cachedQrData;
  }

  logout(modal: any) {
    modal.dismiss();
  }

  ngOnInit(): void {
    this.userInfo = this.authenticationService.UserInfo;

    // initialize flags once
    this.updateSegment();

    // subscribe only to NavigationEnd events (fires when navigation has finished)
    const navSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => {
        // only update when router navigation ends
        this.updateSegment();
      });
    this.subscriptions.add(navSub);

    // keep theme$ as-is (observable)
    this.theme$ = this.store.select(getLayoutColor);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private updateSegment(): void {
    // get the clean url without query params
    const cleanUrl = this.router.url.split('?')[0];
    const segments = cleanUrl.split('/').filter(Boolean);
    this.lastSegment = segments[segments.length - 1] || '';

    // compute booleans ONCE
    this.isHomeActive = cleanUrl.includes('/home');
    this.isTransactionsActive = cleanUrl.includes('/transactions');
    this.isCalculatorActive = cleanUrl.includes('/calculator');

    // for user tab we want to check the final segment equals 'user'
    // this will be true for /user, /user/user and also for /subagent/user etc.
    this.isUserActive = this.lastSegment === 'user';
  }

  // Removed isActive(...) method usage in template (no longer needed)

  userQrCode() {
    return this.getCachedQrCode();
  }

  openModal(content: TemplateRef<HTMLElement>, options: NgbModalOptions) {
    if (this.isReactNativeWebView()) {
      const qrData = this.getCachedQrCode();
      (window as any).ReactNativeWebView.postMessage(
        JSON.stringify({
          type: 'showQRModal',
          payload: { qrData },
        })
      );
    } else {
      this.modalService.open(content, options);
    }
  }

  shareQr(modal: any) {
    const canvas: HTMLCanvasElement | null = this.qrCodeElement?.nativeElement.querySelector('canvas') ?? null;

    if (!canvas) {
      console.error('QR canvas not found');
      return;
    }

    const base64Data = canvas.toDataURL('image/png');

    if (this.isReactNativeWebView()) {
      (window as any).ReactNativeWebView.postMessage(
        JSON.stringify({
          type: 'shareImage',
          payload: {
            base64: base64Data,
            fileName: 'bencash-qr.png',
          },
        })
      );
    } else {
      const link = document.createElement('a');
      link.href = base64Data;
      link.download = 'qr-code.png';
      link.click();
    }
  }
}
